import { useCallback, useEffect, useState } from 'react';
import { LightBoxWrapper } from '../common/LightBoxWrapper';
import { LoadingEllipsis } from '../common/LoadingEllipsis';
import { onPhotoUpload } from '../common/photoupload/photoUploadService';
import type { CropConfig, PhotoUploadConfig, UploadedPhotoPlaceholderProps } from '../common/photoupload/types.d.ts';
import ClassroomPhotoUploadContent from './ClassroomPhotoUploadContent';
import PhotoCropContent, { type CropCoordinates } from './PhotoCropContent';
import ProfilePhotoUploadContent from './ProfilePhotoUploadContent';
import { PhotoProcessingStatus, ServerPhotoTypes, usePhotoProcessingStatus } from './usePhotoProcessingStatus';
import { PHOTO_TYPE, PhotoTypes } from './types';

const ModalSteps = {
    UPLOAD: 'UPLOAD',
    CROPPING: 'CROPPING',
    PROCESSING: 'PROCESSING',
    DONE: 'DONE',
} as const;

type MODAL_STEP = (typeof ModalSteps)[keyof typeof ModalSteps];

interface TeacherPhotoUploadModalProps {
    photoType: PHOTO_TYPE,
    teacherId: number,
    currentPhotoUrl: string | null,
    onPhotoUpdated: () => void,
    onClose: () => void,
    config: PhotoUploadConfig,
}

/**
 * Modal for uploading teacher classroom or profile photos.
 * Steps: upload → processing (poll) → cropping -> done
 */
const TeacherPhotoUploadModal = ({
    photoType,
    teacherId,
    currentPhotoUrl,
    onPhotoUpdated,
    onClose,
    config,
}: TeacherPhotoUploadModalProps) => {
    const [step, setStep] = useState<MODAL_STEP>(ModalSteps.UPLOAD);
    const [allPhotos, setAllPhotos] = useState<UploadedPhotoPlaceholderProps[]>([]);
    const [closeLightbox, setCloseLightbox] = useState(false);
    const [cropSubmitting, setCropSubmitting] = useState(false);
    const [cropConfig, setCropConfig] = useState<CropConfig>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const pollingType = photoType === PhotoTypes.CLASSROOM
        ? ServerPhotoTypes.USER
        : ServerPhotoTypes.TEACHER_PROFILE;

    const processingResult = usePhotoProcessingStatus({
        teacherId,
        type: pollingType,
        enabled: step === ModalSteps.PROCESSING,
    });

    // Transition to processing when a photo upload completes successfully
    useEffect(() => {
        if (step === ModalSteps.UPLOAD &&
                allPhotos.some(p => !p.isLoading && p.photoUrl !== null &&
                        !String(p.photoId).includes('temp'))) {
            setStep(ModalSteps.PROCESSING);
        }
    }, [allPhotos, step]);

    // Transition to cropping when photo processing completes
    useEffect(() => {
        if (step === ModalSteps.PROCESSING && processingResult.status === PhotoProcessingStatus.PROCESSED) {
            // Skip cropping step if on mobile
            if (window.isMobileView()) {
                setStep(ModalSteps.DONE);
                return;
            }

            setStep(ModalSteps.CROPPING);
            const isProfile = photoType === PhotoTypes.PROFILE;
            const sourcePhotoUrl = isProfile
                ? processingResult.paths?.['orig']
                : processingResult.paths?.['src'];
            if (sourcePhotoUrl) {
                setCropConfig({
                    sourcePhotoUrl,
                    circularCrop: isProfile,
                    naturalWidth: processingResult.imageWidth ?? 0,
                    naturalHeight: processingResult.imageHeight ?? 0,
                });
            }
        }
    }, [step, processingResult, photoType]);

    const handlePhotoUpload = useCallback((files: FileList | File[]) => {
        const fileArray = Array.from(files);
        onPhotoUpload(fileArray, {
            allPhotos,
            setAllPhotos,
            config,
            errorCallback: (message?) => {
                if (typeof message === 'string') {
                    setErrorMessage(message);
                }
            },
        });
    }, [allPhotos, config]);

    const handleDeleteRequest = useCallback((photoId: string) => {
        setAllPhotos((prev) => prev.filter((p) => p.photoId !== photoId));
    }, []);

    const handleRemoveProfilePhoto = useCallback(() => {
        $.ajax(`${secureBaseURL}teacher/profile_photo_remove.html`, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
            },
            xhrFields: {
                withCredentials: true,
            },
        }).then((res) => {
            if (res) {
                onPhotoUpdated();
                setCloseLightbox(true);
            }
        });
    }, [onPhotoUpdated]);

    const handleCropSubmit = useCallback((coords: CropCoordinates) => {
        setCropSubmitting(true);

        const params = new URLSearchParams();
        params.set('xCoord', String(coords.xCoord));
        params.set('yCoord', String(coords.yCoord));
        params.set('cropWidth', String(coords.cropWidth));
        params.set('cropHeight', String(coords.cropHeight));
        params.set('naturalWidth', String(coords.naturalWidth));
        params.set('naturalHeight', String(coords.naturalHeight));

        let url;
        if (photoType === PhotoTypes.CLASSROOM) {
            params.set('imageWidth', String(coords.naturalWidth));
            params.set('imageHeight', String(coords.naturalHeight));
            url = `${secureBaseURL}teacher/teacher_photo_crop.html?action=crop&format=json`;
        } else {
            url = `${secureBaseURL}teacher/profile_photo_crop.html`;
        }

        $.ajax(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            xhrFields: {
                withCredentials: true,
            },
            data: params.toString(),
        }).then((res) => {
            const statusStr = res && typeof res === 'object' ? res.result : res;
            if (!statusStr || statusStr.startsWith('Error:')) {
                throw new Error(statusStr);
            }
            setStep(ModalSteps.DONE);
        }).catch((err) => {
            console.error('Crop failed:', err);
            window.showScreenAndOverlayPopupErrors?.({
                message: 'Sorry, cropping failed. Please try again.',
            });
            setCloseLightbox(true);
        }).done(() => {
            setCropSubmitting(false);
        });
    }, [photoType]);

    const handleClose = () => {
        setCloseLightbox(true);
        onPhotoUpdated();
    };

    const uploadedPhoto = allPhotos.length > 0 ? allPhotos[0] : null;

    const renderStep = () => {
        switch (step) {
            case ModalSteps.UPLOAD:
                if (photoType === PhotoTypes.CLASSROOM) {
                    return (
                        <ClassroomPhotoUploadContent
                            onPhotoUpload={handlePhotoUpload}
                            uploadedPhoto={uploadedPhoto}
                            onDeleteRequest={handleDeleteRequest}
                            minImageWidth={config.minImageWidth}
                            minImageHeight={config.minImageHeight}
                            errorMessage={errorMessage}
                            clearErrorMessage={() => { setErrorMessage(null); }}
                        />
                    );
                }
                return (
                    <ProfilePhotoUploadContent
                        onPhotoUpload={handlePhotoUpload}
                        uploadedPhoto={uploadedPhoto}
                        onDeleteRequest={handleDeleteRequest}
                        currentPhotoUrl={currentPhotoUrl}
                        photoIsPublished={!currentPhotoUrl!.includes('placeholder')}
                        onRemovePhoto={handleRemoveProfilePhoto}
                    />
                );

            case ModalSteps.PROCESSING:
                return (
                    <div className="photo-processing-status">
                        {processingResult.status === PhotoProcessingStatus.ERROR || processingResult.status === PhotoProcessingStatus.TIMEOUT
                            ? (
                                <div className="alert-message error">
                                    {processingResult.errorMessage}
                                </div>
                            )
                            : (
                                <>
                                    <p>Processing your photo...</p>
                                    <LoadingEllipsis/>
                                </>
                            )}
                    </div>
                );

            case ModalSteps.CROPPING: {
                if (!cropConfig?.sourcePhotoUrl) {
                    return <p>Unable to load photo for cropping.</p>;
                }
                return (
                    <PhotoCropContent
                        subHeader={photoType === PhotoTypes.CLASSROOM
                            ? 'Check the highlighted area of your photo to make sure this is how you want it to appear.'
                            : 'Put the focus on your face'}
                        photoUrl={cropConfig.sourcePhotoUrl}
                        naturalWidth={processingResult.imageWidth ?? 0}
                        naturalHeight={processingResult.imageHeight ?? 0}
                        minCropSize={config.minImageWidth}
                        circularCrop={cropConfig.circularCrop}
                        onSubmitCrop={handleCropSubmit}
                        submitting={cropSubmitting}
                    />
                );
            }

            case ModalSteps.DONE:
                return (
                    <div className="photo-upload-success">
                        <p>Your photo has been uploaded successfully!</p>
                        {photoType === PhotoTypes.CLASSROOM &&
                                <p className="text-discreet">
                                    Thanks for adding a classroom photo! Our screening team will review it ASAP
                                    and we&apos;ll let you know if it needs any changes.
                                </p>
                        }
                        <button className="button-primary" type="button" onClick={handleClose}>
                            Done
                        </button>
                    </div>
                );
        }
    };

    return (config !== undefined &&
            <LightBoxWrapper
                unMountHandler={onClose}
                closeLightbox={closeLightbox}
                viewportPositioned={true}
                additionalClasses="upload-photo-modal-content"
            >
                <div className="teacher-photo-upload-modal">
                    {renderStep()}
                </div>
            </LightBoxWrapper>
    );
};

export default TeacherPhotoUploadModal;
